import { useState, useCallback } from "react";
import { useTheme } from "../context/ThemeContext";
import { THEME_PRESETS, type ThemePreset } from "../config/theme-presets";

// ============================================================================
// ICONS
// ============================================================================

function CheckIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function ChevronDownIcon({ open }: { open: boolean }) {
  return (
    <svg
      className={`w-5 h-5 transition-transform duration-300 ${open ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function ResetIcon() {
  return (
    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  );
}

// ============================================================================
// PRESET MINI-PREVIEW
// ============================================================================

function PresetPreview({ preset }: { preset: ThemePreset }) {
  const darkBg = preset.semantic.dark.background;
  const darkCard = preset.semantic.dark.card;
  const primary = preset.colors.primary;
  const chartSlice = preset.colors.chart.slice(0, 5);

  return (
    <div
      className="relative w-full h-24 rounded-lg overflow-hidden border border-border/50"
      style={{ background: darkBg }}
    >
      {/* Mini card */}
      <div
        className="absolute top-3 left-3 right-3 h-10 rounded-md"
        style={{
          background: darkCard,
          backdropFilter: preset.cardStyle.backdropBlur !== "none" ? preset.cardStyle.backdropBlur : undefined,
          borderRadius: preset.cardStyle.borderRadius,
        }}
      >
        <div className="flex items-center gap-1.5 p-2">
          <div className="w-2 h-2 rounded-full" style={{ background: primary }} />
          <div className="h-1.5 w-12 rounded-full opacity-40" style={{ background: preset.semantic.dark.foreground }} />
        </div>
      </div>
      {/* Chart color swatches */}
      <div className="absolute bottom-3 left-3 flex gap-1">
        {chartSlice.map((c, i) => (
          <div
            key={i}
            className="w-3 h-3 rounded-full"
            style={{ background: c }}
          />
        ))}
      </div>
      {/* Primary accent bar */}
      <div
        className="absolute bottom-3 right-3 h-4 w-10 rounded"
        style={{
          background: `linear-gradient(135deg, ${preset.colors.gradient.start}, ${preset.colors.gradient.end})`,
        }}
      />
    </div>
  );
}

// ============================================================================
// PRESET CARD
// ============================================================================

function PresetCard({
  preset,
  isActive,
  onSelect,
}: {
  preset: ThemePreset;
  isActive: boolean;
  onSelect: () => void;
}) {
  const fontName = preset.fontFamily.split(",")[0]?.replace(/'/g, "").trim() ?? "System";

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`
        relative text-left w-full p-4 rounded-xl border-2 transition-all duration-300
        ${isActive
          ? "border-[var(--brand-primary)] shadow-lg shadow-[var(--brand-primary)]/10 bg-[var(--brand-primary)]/5"
          : "border-border hover:border-muted-foreground/30 bg-card"
        }
      `}
    >
      {/* Active indicator */}
      {isActive && (
        <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-[var(--brand-primary)] text-white flex items-center justify-center">
          <CheckIcon />
        </div>
      )}

      <PresetPreview preset={preset} />

      <div className="mt-3">
        <h4 className="font-semibold text-foreground">{preset.name}</h4>
        <p className="text-xs text-muted-foreground mt-0.5">{preset.description}</p>
        <p className="text-xs text-muted-foreground/70 mt-1">Font: {fontName}</p>
      </div>
    </button>
  );
}

// ============================================================================
// COLOR PICKER FIELD
// ============================================================================

function ColorField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="relative">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className="w-8 h-8 rounded-lg border border-border cursor-pointer appearance-none bg-transparent [&::-webkit-color-swatch-wrapper]:p-0.5 [&::-webkit-color-swatch]:rounded"
        />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground font-mono">{value}</p>
      </div>
    </div>
  );
}

// ============================================================================
// SLIDER FIELD
// ============================================================================

function SliderField({
  label,
  value,
  min,
  max,
  step,
  unit,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  unit: string;
  onChange: (v: number) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="text-xs text-muted-foreground font-mono">{value}{unit}</p>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="w-full h-1.5 rounded-full bg-muted appearance-none cursor-pointer [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--brand-primary)] [&::-webkit-slider-thumb]:shadow-md"
      />
    </div>
  );
}

// ============================================================================
// FONT OPTIONS
// ============================================================================

const FONT_OPTIONS = [
  { label: "Inter", value: "'Inter', system-ui, sans-serif", url: "https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap" },
  { label: "Plus Jakarta Sans", value: "'Plus Jakarta Sans', system-ui, sans-serif", url: "" },
  { label: "Poppins", value: "'Poppins', system-ui, sans-serif", url: "https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap" },
  { label: "DM Sans", value: "'DM Sans', system-ui, sans-serif", url: "https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&display=swap" },
  { label: "Outfit", value: "'Outfit', system-ui, sans-serif", url: "https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap" },
  { label: "System Default", value: "system-ui, -apple-system, sans-serif", url: "" },
];

// ============================================================================
// CUSTOMIZATION PANEL
// ============================================================================

function CustomizationPanel() {
  const { activeTheme, setCustomTheme, customTheme, resetCustomTheme } = useTheme();
  const [openSection, setOpenSection] = useState<string | null>(null);

  const toggleSection = (id: string) =>
    setOpenSection((prev) => (prev === id ? null : id));

  const handleColorChange = useCallback(
    (key: keyof ThemePreset["colors"], value: string) => {
      setCustomTheme({
        colors: { ...activeTheme.colors, [key]: value } as ThemePreset["colors"],
      });
    },
    [activeTheme.colors, setCustomTheme]
  );

  const handleGradientChange = useCallback(
    (key: "start" | "end", value: string) => {
      setCustomTheme({
        colors: {
          ...activeTheme.colors,
          gradient: { ...activeTheme.colors.gradient, [key]: value },
        } as ThemePreset["colors"],
      });
    },
    [activeTheme.colors, setCustomTheme]
  );

  const handleChartColorChange = useCallback(
    (index: number, value: string) => {
      const newChart = [...activeTheme.colors.chart];
      newChart[index] = value;
      setCustomTheme({
        colors: { ...activeTheme.colors, chart: newChart } as ThemePreset["colors"],
      });
    },
    [activeTheme.colors, setCustomTheme]
  );

  const handleFontChange = useCallback(
    (fontFamily: string, fontUrl: string) => {
      setCustomTheme({ fontFamily, fontUrl });
    },
    [setCustomTheme]
  );

  const handleRadiusChange = useCallback(
    (px: number) => {
      const rem = `${px / 16}rem`;
      setCustomTheme({
        cardStyle: {
          ...activeTheme.cardStyle,
          borderRadius: `${px}px`,
        },
        components: {
          ...activeTheme.components,
          baseRadius: rem,
        },
      });
    },
    [activeTheme.cardStyle, activeTheme.components, setCustomTheme]
  );

  const handleGlassmorphismToggle = useCallback(() => {
    const isGlass = activeTheme.cardStyle.backdropBlur !== "none";
    setCustomTheme({
      cardStyle: {
        ...activeTheme.cardStyle,
        backdropBlur: isGlass ? "none" : "blur(16px)",
      },
    });
  }, [activeTheme.cardStyle, setCustomTheme]);

  const handleScrollbarWidthChange = useCallback(
    (px: number) => {
      setCustomTheme({
        scrollbar: { ...activeTheme.scrollbar, width: `${px}px` },
      });
    },
    [activeTheme.scrollbar, setCustomTheme]
  );

  const handleScrollbarColorChange = useCallback(
    (color: string) => {
      setCustomTheme({
        scrollbar: { ...activeTheme.scrollbar, thumbColor: color },
      });
    },
    [activeTheme.scrollbar, setCustomTheme]
  );

  const hasCustomizations = customTheme && Object.keys(customTheme).length > 0;

  const currentRadiusPx = parseFloat(activeTheme.cardStyle.borderRadius) || 12;
  const currentScrollbarPx = parseFloat(activeTheme.scrollbar.width) || 8;
  const isGlassmorphism = activeTheme.cardStyle.backdropBlur !== "none";
  const currentFontLabel =
    FONT_OPTIONS.find((f) => f.value === activeTheme.fontFamily)?.label ?? "Custom";

  const sections = [
    {
      id: "colors",
      title: "Colors",
      content: (
        <div className="space-y-4">
          <ColorField label="Primary" value={activeTheme.colors.primary} onChange={(v) => handleColorChange("primary", v)} />
          <ColorField label="Secondary" value={activeTheme.colors.secondary} onChange={(v) => handleColorChange("secondary", v)} />
          <ColorField label="Accent" value={activeTheme.colors.accent} onChange={(v) => handleColorChange("accent", v)} />
          <ColorField label="Ring" value={activeTheme.colors.ring} onChange={(v) => handleColorChange("ring", v)} />
          <ColorField label="Gradient Start" value={activeTheme.colors.gradient.start} onChange={(v) => handleGradientChange("start", v)} />
          <ColorField label="Gradient End" value={activeTheme.colors.gradient.end} onChange={(v) => handleGradientChange("end", v)} />
        </div>
      ),
    },
    {
      id: "typography",
      title: "Typography",
      content: (
        <div className="space-y-4">
          <div>
            <p className="text-sm font-medium text-foreground mb-2">Font Family</p>
            <div className="grid grid-cols-2 gap-2">
              {FONT_OPTIONS.map((font) => (
                <button
                  key={font.label}
                  type="button"
                  onClick={() => handleFontChange(font.value, font.url)}
                  className={`
                    px-3 py-2 rounded-lg text-sm border transition-all
                    ${currentFontLabel === font.label
                      ? "border-[var(--brand-primary)] bg-[var(--brand-primary)]/10 text-[var(--brand-primary)] font-medium"
                      : "border-border text-muted-foreground hover:border-muted-foreground/50"
                    }
                  `}
                  style={{ fontFamily: font.value }}
                >
                  {font.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: "cards",
      title: "Cards & Layout",
      content: (
        <div className="space-y-5">
          <SliderField
            label="Border Radius"
            value={currentRadiusPx}
            min={4}
            max={24}
            step={1}
            unit="px"
            onChange={handleRadiusChange}
          />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-foreground">Glassmorphism</p>
              <p className="text-xs text-muted-foreground">Frosted glass card backgrounds</p>
            </div>
            <button
              type="button"
              onClick={handleGlassmorphismToggle}
              className={`
                relative w-11 h-6 rounded-full transition-all duration-300 cursor-pointer
                ${isGlassmorphism
                  ? "bg-gradient-to-r from-[var(--brand-primary)] to-[var(--brand-gradient-end)] shadow-lg shadow-[var(--brand-primary)]/25"
                  : "bg-gray-200 dark:bg-gray-600"
                }
              `}
            >
              <span
                className={`
                  absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow-md
                  transition-transform duration-300 ease-out
                  ${isGlassmorphism ? "translate-x-5" : "translate-x-0"}
                `}
              />
            </button>
          </div>
        </div>
      ),
    },
    {
      id: "chart-colors",
      title: "Chart Colors",
      content: (
        <div className="grid grid-cols-4 gap-3">
          {activeTheme.colors.chart.map((color, i) => (
            <div key={i} className="text-center">
              <input
                type="color"
                value={color}
                onChange={(e) => handleChartColorChange(i, e.target.value)}
                className="w-10 h-10 rounded-lg border border-border cursor-pointer appearance-none bg-transparent [&::-webkit-color-swatch-wrapper]:p-1 [&::-webkit-color-swatch]:rounded"
              />
              <p className="text-[10px] text-muted-foreground mt-1">{i + 1}</p>
            </div>
          ))}
        </div>
      ),
    },
    {
      id: "scrollbar",
      title: "Scrollbar",
      content: (
        <div className="space-y-5">
          <SliderField
            label="Width"
            value={currentScrollbarPx}
            min={4}
            max={14}
            step={1}
            unit="px"
            onChange={handleScrollbarWidthChange}
          />
          <ColorField
            label="Thumb Color"
            value={activeTheme.scrollbar.thumbColor.startsWith("#") ? activeTheme.scrollbar.thumbColor : activeTheme.colors.primary}
            onChange={handleScrollbarColorChange}
          />
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-foreground">Customize</h3>
        {hasCustomizations && (
          <button
            type="button"
            onClick={resetCustomTheme}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
          >
            <ResetIcon />
            Reset to Preset
          </button>
        )}
      </div>

      <div className="space-y-2">
        {sections.map((section) => (
          <div key={section.id} className="border border-border rounded-xl overflow-hidden">
            <button
              type="button"
              onClick={() => toggleSection(section.id)}
              className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/50 transition-colors"
            >
              <span className="text-sm font-medium text-foreground">{section.title}</span>
              <ChevronDownIcon open={openSection === section.id} />
            </button>
            {openSection === section.id && (
              <div className="px-4 pb-4 border-t border-border pt-4">
                {section.content}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================================
// MAIN EXPORT
// ============================================================================

export function ThemeSwitcher() {
  const { activePresetId, setPreset } = useTheme();

  return (
    <div className="space-y-8">
      {/* Section 1: Preset Cards */}
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-1">Theme Presets</h3>
        <p className="text-sm text-muted-foreground mb-4">
          Choose a preset to instantly change the look and feel of your dashboard.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {THEME_PRESETS.map((preset) => (
            <PresetCard
              key={preset.id}
              preset={preset}
              isActive={activePresetId === preset.id}
              onSelect={() => setPreset(preset.id)}
            />
          ))}
        </div>
      </div>

      {/* Section 2: Customization */}
      <CustomizationPanel />
    </div>
  );
}
